import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getLocationQuery, getPageNumberInQuery } from '@/logic/app/reducer'
import { isUserAgrarian } from '@/logic/auth/reducer'
import { useData } from '@/logic/data'
import { TemplatesFilterName } from '@/logic/harvestSale/constants'
import type { IFetchTemplatesPayload } from '@/logic/harvestSale/interfaces'
import { getIndicators } from '@/logic/harvestSale/reducer'
import { fetchTemplates as fetchHarvestSaleTemplates } from '@/logic/harvestSale/thunks'
import { storePlaceholder } from '@/pages/HarvestSaleLots/constants'
import { getCheckboxValuesFromQuery, LotsRegionCodesStorage, saveFilterValuesToQuery } from '@/pages/HarvestSaleLots/helpers'
import type { THarvestSaleFormValues } from '@/pages/HarvestSaleLots/interfaces'
import type { RootState } from '@/redux/interfaces'
import type { IHarvestSaleCulture } from '@/types/HarvestSaleLots'
import { EPriceVariant } from '@/types/HarvestSaleLots'
import { useEffectAfterMount, useMount } from '@/utils/hooks'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { changeQueryString } from '@/utils/url'
import { useHarvestSaleLotsPageParams } from './useHarvestSaleLotsPageParams'

const mapState = (state: RootState) => ({
  pageNumber: getPageNumberInQuery(state),
  isAgrarian: isUserAgrarian()(state),
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  search: getLocationQuery(state) as Record<string, any>,
  indicators: getIndicators()(state),
})

export const useHarvestSaleLotsFormApi = (itemsPerPage: number) => {
  const { pageNumber, isAgrarian, search, indicators } = useSelector(mapState)
  const dispatch = useDispatch()
  const { cultureId } = useHarvestSaleLotsPageParams()

  const { isMobile } = useDeviceType()

  const initialValues: THarvestSaleFormValues = useMemo(() => {
    //eslint-disable-next-line @typescript-eslint/no-explicit-any
    const values: Record<string, any> = {
      pageNumber: search?.pageNumber,
      cultureId,
      sortBy: search?.sortBy || EPriceVariant.ELEVATOR,
      buyers: getCheckboxValuesFromQuery(search?.[TemplatesFilterName.BUYERS]),
      elevators: getCheckboxValuesFromQuery(search?.[TemplatesFilterName.ELEVATORS]),
    }
    if (isAgrarian) values.store = storePlaceholder

    if (search?.['cultureClass']) {
      values.cultureClass = !Array.isArray(search?.['cultureClass']) ? [search?.['cultureClass']] : search?.['cultureClass']
    }

    if (search?.['proteinFrom']) {
      values.proteinFrom = !Array.isArray(search?.['proteinFrom']) ? [search?.['proteinFrom']] : search?.['proteinFrom']
    }

    if (search?.['regionCodes']) {
      values.regionCodes = !Array.isArray(search?.['regionCodes']) ? [search?.['regionCodes']] : []
    }

    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const [savedFilterValues, saveFilterValues] = useState(initialValues)

  const fetchTemplates = (payload: IFetchTemplatesPayload) => {
    const regionCodes = payload?.filterValues?.regionCodes

    if (regionCodes && (regionCodes[0] === '' || regionCodes[0] === 0)) {
      regionCodes?.splice(0, 1)
    }

    const filterValues = payload?.filterValues
    dispatch(fetchHarvestSaleTemplates({ ...payload, indicators, filterValues }))
  }

  useEffect(() => {
    if (savedFilterValues.regionCodes?.length) LotsRegionCodesStorage.set(savedFilterValues.regionCodes)
  }, [savedFilterValues.regionCodes])

  useEffect(() => {
    saveFilterValuesToQuery(savedFilterValues)
  }, [savedFilterValues])

  useMount(() => {
    fetchTemplates({
      pageNumber,
      filterValues: savedFilterValues,
      maxShownItemsCount: isMobile ? itemsPerPage * pageNumber : undefined,
    })
  })

  useEffectAfterMount(() => {
    fetchTemplates({
      pageNumber,
      filterValues: savedFilterValues,
      maxShownItemsCount: isMobile ? itemsPerPage * pageNumber : undefined,
    })
  }, [savedFilterValues.store, savedFilterValues.sortBy, pageNumber])

  const { data: cultures = [] } = useData<IHarvestSaleCulture[]>('cultures')

  const submitFilterForm = (filterValues: THarvestSaleFormValues) => {
    saveFilterValues(filterValues)
    fetchTemplates({ filterValues })
  }

  const resetFilterForm = () => {
    saveFilterValues({ cultureId })
    fetchTemplates({ pageNumber })
  }

  const onChangePage = (pageNumberUpdated: number | undefined) => {
    changeQueryString('pageNumber', pageNumberUpdated || pageNumber, true)
  }

  return {
    onChangePage,
    submitFilterForm,
    resetFilterForm,
    saveFilterValues,
    savedFilterValues,
    cultures,
  }
}
